import React, { useState, useEffect, useCallback } from 'react';
import { SllModule } from '@types';
import SectionContent from '../section-content/SectionContent';
import { useEditLesson } from '../../useEditLesson';
import { useSllMoveModuleMutation, SllModuleType } from '@api/graphql-codegen';
import type { DropResult, ResponderProvided, DraggableProvided, DroppableProvided, DraggableStateSnapshot } from '@src/utils/componentCasts';
import { DragDropContext, Droppable, Draggable } from '@src/utils/componentCasts';

type Props = {
  contents: SllModule[];
};

const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

function SectionContents(props: Props) {
  const { contents } = props;
  const [ localContents, setLocalContents ] = useState<Array<SllModule>>(contents);
  const { content: selectedContent, lesson: selectedLesson, setSelectionLevel, setLesson, setContent } = useEditLesson();
  const [sllMoveModule] = useSllMoveModuleMutation();

  useEffect(() => {
    setLocalContents(contents ?? []);
  }, [contents]);

  const handleDragEnd = useCallback(async (result: DropResult, provided?: ResponderProvided) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    const reorderedSubItems = reorder(
      localContents,
      sourceIndex,
      destIndex
    );

    const selectedModule = (reorderedSubItems as SllModule[]).find((content: SllModule, index) => index === destIndex);
    setLocalContents(reorderedSubItems as SllModule[]);
    if (selectedLesson && selectedModule) {
      const moved = await sllMoveModule({
        variables: {
          input: {
            lessonId: selectedLesson?.id,
            id: selectedModule.id,
            type: SllModuleType.PRESENTATION,
            oldPosition: {
              sectionId: undefined,
              position: result.source!.index + 1
            },
            newPosition: {
              sectionId: undefined,
              position: result.destination!.index + 1
            }
          }
        }
      });
      if (moved.data?.sllMoveModule) {
        setSelectionLevel('Lesson');
        setLesson(selectedLesson);
        setContent(undefined);
      }
    }
  }, [localContents, selectedLesson, sllMoveModule, setContent, setLesson, setSelectionLevel]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={selectedLesson!.id} type="droppableModuleItem">
        {(droppableProvided: DroppableProvided) => (
          <div
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
          >
            {localContents.length > 0 ?
              localContents.map((item: SllModule, index: number) => (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                >
                  {(
                    draggableProvided: DraggableProvided,
                    snapshot: DraggableStateSnapshot
                  ) => {
                    const sectionSelected = selectedContent?.id === item.id;
                    return (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                      >
                        <SectionContent draggableProvided={draggableProvided} snapshot={snapshot} modules={contents} key={item.id} selected={sectionSelected} content={item}/>
                      </div>
                    );
                  }}
                </Draggable>
              )) :
              <div style={{color:'white', height:'1px'}}>{}</div>}
            {droppableProvided.placeholder as any}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default SectionContents;
